/**
 * Flow Graph Engine — builds and manages the flow tree.
 * Port of Python graph_builder.py (FlowGraphBuilder + FlowTreeManager).
 *
 * Shared across Event Flow and Screen Flow sections.
 */
import type { FlowRecord, FlowNodeData, FlowLevel } from './flow.types';

/* ─── KPI Percentage Map ──────────────────────────────────────────────────── */

export type KpiPercentages = Record<string, number>;
export type CrossSessionKpiPercentages = Record<string, number>;

/** Tuple: [event, count, pct, avgTimeSec] */
export type ChildTuple = [string, number, number, number | null];

/* ─── FlowGraphBuilder ────────────────────────────────────────────────────── */

/**
 * Builds a graph structure from flow records (FIRST_DIST + TRANSITION).
 * Each transition is keyed by the full prefix path for path-aware counting.
 */
export class FlowGraphBuilder {
  totalFlows = 0;
  firstNodes: Array<ChildTuple> = [];
  /** key = "prefixPath||event||seq" → Map<childEvent, flows> */
  private graph = new Map<string, Map<string, number>>();
  /** Parallel map: same key as graph → Map<childEvent, avgTimeSec> */
  private timings = new Map<string, Map<string, number | null>>();
  kpiPercentages: KpiPercentages = {};
  crossSessionKpiPercentages: CrossSessionKpiPercentages = {};

  constructor(records: FlowRecord[]) {
    this.build(records);
  }

  /* ── Build ─────────────────────────────────────────────────────────────── */

  private build(records: FlowRecord[]) {
    const firstDist = records.filter((r) => r.kind === 'FIRST_DIST');

    this.totalFlows = firstDist.reduce((sum, r) => sum + r.flows, 0);
    if (this.totalFlows > 0) {
      this.firstNodes = firstDist
        .map((r): ChildTuple => [r.a, r.flows, r.flows / this.totalFlows, null])
        .sort((a, b) => b[1] - a[1]);
    }

    const transitions = records.filter((r) => r.kind === 'TRANSITION');
    for (const r of transitions) {
      const key = makeKey(r.prefix_path ?? '', r.a, r.seq ?? 0);

      let children = this.graph.get(key);
      if (!children) {
        children = new Map();
        this.graph.set(key, children);
      }
      children.set(r.b!, r.flows);

      let timingChildren = this.timings.get(key);
      if (!timingChildren) {
        timingChildren = new Map();
        this.timings.set(key, timingChildren);
      }
      timingChildren.set(r.b!, r.avg_time_sec ?? null);
    }

    // Extract KPI percentages from records
    for (const r of records) {
      if (r.prefix_path) {
        if (r.kpi_percentage != null) {
          this.kpiPercentages[r.prefix_path] = r.kpi_percentage;
        }
        if (r.cross_session_kpi_percentage != null) {
          this.crossSessionKpiPercentages[r.prefix_path] = r.cross_session_kpi_percentage;
        }
      }
    }
  }

  /* ── Queries ───────────────────────────────────────────────────────────── */

  getFirstNodes(
    minCount = 0,
    textFilter = '',
  ): Array<ChildTuple> {
    return this.firstNodes.filter(([event, count]) => {
      if (count < minCount) return false;
      if (textFilter && !event.toLowerCase().includes(textFilter.toLowerCase())) return false;
      return true;
    });
  }

  /**
   * Get children of a parent node at a specific position in the path.
   * Includes a "(none)" terminal node for flows that end at this point.
   */
  getChildren(
    parentEvent: string,
    parentSeq: number,
    parentCount: number,
    currentPath: string[],
    minCount = 0,
    textFilter = '',
  ): Array<ChildTuple> {
    const result: Array<ChildTuple> = [];
    let childrenTotalRaw = 0;

    const prefixPathStr = pathToString(currentPath);
    const key = makeKey(prefixPathStr, parentEvent, parentSeq);
    const children = this.graph.get(key);
    const timingChildren = this.timings.get(key);

    if (children) {
      for (const [event, count] of children) {
        childrenTotalRaw += count;
        if (count < minCount) continue;
        if (textFilter && !event.toLowerCase().includes(textFilter.toLowerCase())) continue;
        const pct = parentCount > 0 ? count / parentCount : 0;
        const avgTime = timingChildren?.get(event) ?? null;
        result.push([event, count, pct, avgTime]);
      }
    }

    // Terminal node for flows that don't continue
    const terminalCount = parentCount - childrenTotalRaw;
    if (terminalCount > 0 && terminalCount >= minCount) {
      const pct = parentCount > 0 ? terminalCount / parentCount : 0;
      result.push(['(none)', terminalCount, pct, null]);
    }

    result.sort((a, b) => b[1] - a[1]);
    return result;
  }

  /**
   * Merge continuation flow records into the graph under a session-specific namespace.
   * The continuation records are from a separate BigQuery query for the next session.
   * They are stored with a session-prefixed key to avoid collisions with session 0 data.
   */
  mergeContinuation(records: FlowRecord[], sessionIndex: number): void {
    const prefix = `__s${sessionIndex}__`;

    const firstDist = records.filter(r => r.kind === 'FIRST_DIST');
    const transitions = records.filter(r => r.kind === 'TRANSITION');

    // Store first nodes under the session prefix
    const sessionFirstNodes: Array<ChildTuple> = [];
    const sessionTotal = firstDist.reduce((sum, r) => sum + r.flows, 0);
    if (sessionTotal > 0) {
      for (const r of firstDist) {
        sessionFirstNodes.push([r.a, r.flows, r.flows / sessionTotal, r.avg_time_sec ?? null]);
      }
      sessionFirstNodes.sort((a, b) => b[1] - a[1]);

      // Compute weighted average none_duration across FIRST_DIST groups
      let noneDurTotal = 0;
      let noneDurWeight = 0;
      for (const r of firstDist) {
        if (r.none_duration_sec != null) {
          noneDurTotal += r.none_duration_sec * r.flows;
          noneDurWeight += r.flows;
        }
      }
      if (noneDurWeight > 0) {
        this.noneDurations.set(sessionIndex, noneDurTotal / noneDurWeight);
      }
    }

    // Store session first nodes on a special key
    this.continuationFirstNodes.set(prefix, sessionFirstNodes);
    this.continuationTotals.set(prefix, sessionTotal);

    // Merge transitions into the graph with session-prefixed keys
    for (const r of transitions) {
      const prefixedPath = prefix + (r.prefix_path ?? '');
      const key = makeKey(prefixedPath, r.a, r.seq ?? 0);

      let children = this.graph.get(key);
      if (!children) {
        children = new Map();
        this.graph.set(key, children);
      }
      children.set(r.b!, r.flows);

      let timingChildren = this.timings.get(key);
      if (!timingChildren) {
        timingChildren = new Map();
        this.timings.set(key, timingChildren);
      }
      timingChildren.set(r.b!, r.avg_time_sec ?? null);
    }

    // Merge KPI percentages with session prefix
    for (const r of records) {
      if (r.prefix_path) {
        if (r.kpi_percentage != null) {
          this.kpiPercentages[prefix + r.prefix_path] = r.kpi_percentage;
        }
        if (r.cross_session_kpi_percentage != null) {
          this.crossSessionKpiPercentages[prefix + r.prefix_path] = r.cross_session_kpi_percentage;
        }
      }
    }
  }

  /**
   * Get first nodes for a continuation session.
   */
  getContinuationFirstNodes(sessionIndex: number): Array<ChildTuple> {
    const prefix = `__s${sessionIndex}__`;
    return this.continuationFirstNodes.get(prefix) ?? [];
  }

  /**
   * Get children for a continuation session's node.
   */
  getContinuationChildren(
    parentEvent: string,
    parentSeq: number,
    parentCount: number,
    currentPath: string[],
    sessionIndex: number,
    minCount = 0,
    textFilter = '',
  ): Array<ChildTuple> {
    const prefix = `__s${sessionIndex}__`;
    const result: Array<ChildTuple> = [];
    let childrenTotalRaw = 0;

    const prefixPathStr = prefix + pathToString(currentPath);
    const key = makeKey(prefixPathStr, parentEvent, parentSeq);
    const children = this.graph.get(key);
    const timingChildren = this.timings.get(key);

    if (children) {
      for (const [event, count] of children) {
        childrenTotalRaw += count;
        if (count < minCount) continue;
        if (textFilter && !event.toLowerCase().includes(textFilter.toLowerCase())) continue;
        const pct = parentCount > 0 ? count / parentCount : 0;
        const avgTime = timingChildren?.get(event) ?? null;
        result.push([event, count, pct, avgTime]);
      }
    }

    const terminalCount = parentCount - childrenTotalRaw;
    if (terminalCount > 0 && terminalCount >= minCount) {
      const pct = parentCount > 0 ? terminalCount / parentCount : 0;
      result.push(['(none)', terminalCount, pct, null]);
    }

    result.sort((a, b) => b[1] - a[1]);
    return result;
  }

  /** Storage for continuation first-node data keyed by session prefix */
  continuationFirstNodes = new Map<string, Array<ChildTuple>>();
  continuationTotals = new Map<string, number>();
  /** Average duration from the last included event to the actual session end, per session index.
   *  Used to display timing on the (none) node that was continued from. */
  noneDurations = new Map<number, number>();
}

/* ─── FlowTreeManager ─────────────────────────────────────────────────────── */

/**
 * Manages the tree state for the flow visualization.
 * Handles expansion, collapse, path tracking.
 */
export class FlowTreeManager {
  levels: FlowLevel[] = [];
  startingCount: number | null = null;
  startingKpiCount: number | null = null;

  constructor(private graphBuilder: FlowGraphBuilder) {}

  /** Initialize level 0 with first-event distribution. */
  initialize(minCount = 0, textFilter = '') {
    const firstNodes = this.graphBuilder.getFirstNodes(minCount, textFilter);
    this.levels = [
      {
        level: 0,
        selected: null,
        nodes: firstNodes.map(([event, count, pct, avgTimeSec]) => ({ event, count, pct, avgTimeSec })),
      },
    ];
  }

  /** Expand a node: prune after its level, add children as next level. */
  expandLevel(level: number, event: string, minCount = 0, textFilter = '') {
    if (level >= this.levels.length) return;

    this.levels = this.levels.slice(0, level + 1);
    this.levels[level].selected = event;

    const parentCount =
      this.levels[level].nodes.find((n) => n.event === event)?.count ?? 0;
    const parentSeq = this.getSeq(level);

    const currentPath = this.getPathUpTo(level);

    const children = this.graphBuilder.getChildren(
      event,
      parentSeq,
      parentCount,
      currentPath,
      minCount,
      textFilter,
    );

    if (children.length > 0) {
      this.levels.push({
        level: level + 1,
        selected: null,
        nodes: children.map(([e, c, p, t]) => ({ event: e, count: c, pct: p, avgTimeSec: t })),
      });
    }
  }

  /** Collapse: select a terminal node without expanding. */
  selectTerminal(level: number, event: string) {
    if (level >= this.levels.length) return;
    this.levels = this.levels.slice(0, level + 1);
    this.levels[level].selected = event;
  }

  /**
   * Expand a "(none)" node with continuation data from the next session.
   * Replaces the "(none)" selection at the given level with continuation first nodes.
   */
  expandContinuation(level: number, sessionIndex: number) {
    if (level >= this.levels.length) return;

    // Mark the (none) node as selected
    this.levels = this.levels.slice(0, level + 1);
    this.levels[level].selected = '(none)';

    // Get first nodes from the continuation session
    const continuationFirst = this.graphBuilder.getContinuationFirstNodes(sessionIndex);
    if (continuationFirst.length === 0) return;

    this.levels.push({
      level: level + 1,
      selected: null,
      nodes: continuationFirst.map(([event, count, pct, avgTimeSec]) => ({ event, count, pct, avgTimeSec })),
      sessionIndex,
    });
  }

  /**
   * Expand a node within a continuation session.
   * Uses the session-prefixed graph data.
   */
  expandContinuationLevel(level: number, event: string, sessionIndex: number, minCount = 0, textFilter = '') {
    if (level >= this.levels.length) return;

    this.levels = this.levels.slice(0, level + 1);
    this.levels[level].selected = event;

    const parentCount =
      this.levels[level].nodes.find(n => n.event === event)?.count ?? 0;
    const parentSeq = this.getContinuationSeq(level, sessionIndex);

    const currentPath = this.getContinuationPathUpTo(level, sessionIndex);

    const children = this.graphBuilder.getContinuationChildren(
      event,
      parentSeq,
      parentCount,
      currentPath,
      sessionIndex,
      minCount,
      textFilter,
    );

    if (children.length > 0) {
      this.levels.push({
        level: level + 1,
        selected: null,
        nodes: children.map(([e, c, p, t]) => ({ event: e, count: c, pct: p, avgTimeSec: t })),
        sessionIndex,
      });
    }
  }

  /**
   * Get the sequence number for a continuation level.
   * Continuation sessions restart their seq from 1.
   */
  private getContinuationSeq(level: number, sessionIndex: number): number {
    let seq = 0;
    for (let i = 0; i <= level; i++) {
      if (this.levels[i].sessionIndex === sessionIndex) {
        seq++;
      }
    }
    return seq;
  }

  private getContinuationPathUpTo(level: number, sessionIndex: number): string[] {
    const path: string[] = [];
    for (let i = 0; i <= level; i++) {
      if (this.levels[i].sessionIndex === sessionIndex && this.levels[i].selected) {
        path.push(this.levels[i].selected!);
      }
    }
    return path;
  }

  /** Get path from selected events through all levels. */
  getPath(): string[] {
    return this.levels
      .filter((l) => l.selected && l.selected !== '(none)')
      .map((l) => l.selected!);
  }

  /** Get KPI percentage for a given path. */
  getKpiPercentage(path: string[]): number | null {
    const pathStr = pathToString(path);
    return this.graphBuilder.kpiPercentages[pathStr] ?? null;
  }

  /* ── Helpers ───────────────────────────────────────────────────────────── */

  private getPathUpTo(level: number): string[] {
    const path: string[] = [];
    for (let i = 0; i <= level; i++) {
      if (this.levels[i].selected) path.push(this.levels[i].selected!);
    }
    return path;
  }

  private getSeq(level: number): number {
    return level + 1;
  }
}

/* ─── Utilities ───────────────────────────────────────────────────────────── */

function makeKey(prefixPath: string, event: string, seq: number): string {
  return `${prefixPath}||${event}||${seq}`;
}

/**
 * Convert a path array to the Python-style tuple string that BigQuery produces.
 * e.g. ['app_launch'] → "('app_launch',)"
 * e.g. ['app_launch', 'home'] → "('app_launch', 'home')"
 */
export function pathToString(path: string[]): string {
  const inner = path.map((e) => `'${e}'`).join(', ');
  return path.length === 1 ? `(${inner},)` : `(${inner})`;
}
